import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Button, Skeleton, cn, useIsMobile, useModal } from '@dashboard/ui'
import { getMotionProps } from '@/libs'
import { HubFirstPublishModal } from '@/layouts/hub'
import { GetStartedStepState } from '../types'
import { StepProgressCircle } from './StepProgressCircle'

/**
 * Props for the StepNext component.
 *
 * @property {Object} progress - The progress information for the current step.
 * @property {number} progress.completed - The number of completed steps.
 * @property {number} progress.total - The total number of steps.
 * @property {GetStartedStepState} [next] - The state information for the next step, if available.
 */
interface StepNextProps {
  progress: { completed: number; total: number }
  next?: GetStartedStepState
}

const stepNextMotionProps = getMotionProps({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
})

export const StepNext = ({ progress, next }: StepNextProps) => {
  const { t } = useTranslation()
  const openPublishModal = useModal(props => <HubFirstPublishModal {...props} />)
  const isCompleted = progress.completed == progress.total
  const canPublish = !!next && isCompleted
  const isMobile = useIsMobile()

  return (
    <motion.div {...stepNextMotionProps} layout="position" className={cn('flex flex-row items-center gap-6')}>
      <StepProgressCircle progress={progress} />
      <div className="w-full">
        <div className="mb-1 text-paragraph-sm font-semibold text-text-secondary">
          {next?.title || t('get-started.next.title')}
        </div>
        <div className="text-paragraph-sm text-text-tertiary">{next?.desc || t('get-started.next.desc')}</div>
      </div>
      {!isMobile && (
        <div className="flex">
          <Button
            className="shrink-0"
            variant="default"
            disabled={!canPublish}
            size="md"
            onClick={() => openPublishModal()}
          >
            {t('get-started.next.publish')}
          </Button>
        </div>
      )}
    </motion.div>
  )
}

export const StepNextSkeleton = () => (
  <motion.div {...stepNextMotionProps} layout="position" className="flex gap-6">
    <Skeleton className="size-16 shrink-0 rounded-full" />
    <div className="w-full">
      <Skeleton className="mb-1 h-5 w-1/4" />
      <Skeleton className="mb-0.5 h-[18px] w-2/3" />
      <Skeleton className="h-[18px] w-2/4" />
    </div>
  </motion.div>
)
